import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { store } from '@/lib/states/store'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Workout,
  Exercise,
} from '@/components/workouts/workoutsColumns'
import EditWorkoutView from '@/components/workouts/editWorkoutView'
import { v4 as uuidv4 } from 'uuid'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChevronDown, Download, Plus, Slash, MoreHorizontal, Trash2 } from 'lucide-react'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils, trpcVanilla } from '@/lib/trpc/vanilla'
import { toTrainingSavePlanInput } from '@/lib/training/savePlanInput'
import {
  apiWorkoutToEditor,
  editorWorkoutToSavePayload,
  ensureWorkoutSections,
  createEmptyEditorWorkout,
  cloneEditorWorkout,
} from '@/lib/utils/workoutSections'
import { requirePermission } from '@/lib/permissions/permissions'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import { toast } from 'sonner'
import AlertBlocker from '@/components/AlertBlocker'
import LoadingSpinner from '@/components/LoadingSpinner'
import ExerciseSelectList from '@/components/workouts/ExerciseSelectList'
import ImportWorkoutDialog from '@/components/workouts/importWorkoutDialog'
import AiWorkoutBar from '@/components/workouts/ai/AiWorkoutBar'
import WorkoutEditorWorkspace from '@/components/workouts/WorkoutEditorWorkspace'
import { createWorkoutExercise } from '@/components/workouts/workoutEditorUtils'
import { Tables } from '@/lib/supabase/supabaseTypes'
import { useNavbar } from '@/components/navbar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import DeleteAlert from '@/components/DeleteAlert'
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ClientTrainingCalendarView,
  RelativeScheduleItem,
} from '@/components/training/ClientTrainingCalendarView'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import { useEditorHistory } from '@/lib/hooks/useEditorHistory'
import type { EditorCommitOptions } from '@/lib/hooks/useEditorHistory'
import { toTrainingTemplateEditorDocument } from '@/lib/editorHistory/documents'
import { EditorUndoControls } from '@/components/planHistory/EditorUndoControls'
import InputFieldEditButton from '@/components/inputFieldEditButton'
import {
  editorPageRailClassName,
  editorPageWorkspaceClassName,
} from '@/components/editor/pageLayout'
import { cn } from '@/lib/utils/ui'
import VersionHistorySheet, {
  PlanHistoryButton,
  PlanVersionTag,
} from '@/components/planHistory/VersionHistorySheet'
import {
  useVersionHistoryOpen,
  useVersionHistoryPreview,
} from '@/components/planHistory/VersionHistoryHost'
import {
  extraStoredIds,
  scheduleFromTrainingSnapshot,
  workoutsFromTrainingSnapshot,
  type TrainingRevisionSnapshot,
} from '@/lib/planHistory/hydrateRevision'
import { liveTrainingSnapshotFromEditor } from '@/lib/planHistory/liveSnapshot'

const WORKOUT_SIDEBAR_STICKY_CLASS =
  'top-[4.75rem] h-[calc(100svh-9rem)]'

type TemplatePlanRow = Awaited<
  ReturnType<typeof trpcVanilla.training.getPlan.query>
>

const formatWorkouts = (plan: TemplatePlanRow | null) => {
  if (!plan) return [] as Workout[]
  const sortedWorkouts = [...(plan.workouts ?? [])].sort(
    (a, b) => (a.day ?? 0) - (b.day ?? 0),
  )
  return sortedWorkouts.map((w) =>
    apiWorkoutToEditor({
      ...w,
      day: w.day ?? 0,
    }),
  )
}

const formatRelativeScheduleFromPlan = (plan: TemplatePlanRow | null) => {
  if (!plan?.schedule_entries) return [] as RelativeScheduleItem[]
  return plan.schedule_entries
    .filter(
      (entry) =>
        entry.schedule_basis === 'relative' &&
        typeof entry.day_offset === 'number',
    )
    .map((entry) => ({
      id: entry.id ?? uuidv4(),
      workout_id: entry.workout_id,
      day_offset: entry.day_offset ?? 0,
      order_index: entry.order_index ?? 0,
    }))
    .sort(
      (a, b) =>
        (a.day_offset ?? 0) - (b.day_offset ?? 0) ||
        (a.order_index ?? 0) - (b.order_index ?? 0),
    )
}

const emptyWorkout = (day: number): Workout =>
  createEmptyEditorWorkout({
    name: 'New workout',
    description: null,
    day,
  })

const toTemplateEditorDocument = (plan: TemplatePlanRow) => {
  const schedulingMode = plan.scheduling_mode === 'dated' ? 'dated' : 'fixed'
  const formatted = formatWorkouts(plan)
  return toTrainingTemplateEditorDocument({
    planName: plan.name ?? '',
    workouts:
      formatted.length === 0 && schedulingMode !== 'dated'
        ? [emptyWorkout(0)]
        : formatted,
    relativeScheduleItems: formatRelativeScheduleFromPlan(plan),
    schedulingMode,
    deletedWorkouts: [],
  })
}

const nextRelativeScheduleItem = (
  items: {
    id?: string
    workout_id: string
    day_offset: number
    order_index: number
  }[],
  workoutId: string,
  dayOffset: number,
): RelativeScheduleItem => {
  const dayItems = items.filter((item) => item.day_offset === dayOffset)
  const nextOrder =
    dayItems.length > 0
      ? Math.max(...dayItems.map((item) => item.order_index ?? 0)) + 1
      : 0
  return {
    id: uuidv4(),
    workout_id: workoutId,
    day_offset: dayOffset,
    order_index: nextOrder,
  }
}

const cloneWorkoutAtDay = (sourceWorkout: Workout, day: number): Workout =>
  cloneEditorWorkout(sourceWorkout, { day })

const removeExerciseFromWorkout = (workout: Workout, exercise: Exercise): Workout => {
  const newExerciseCollection = workout.exerciseCollection.map((group) => [
    ...group,
  ])
  const exerciseGroup = newExerciseCollection[exercise.ex_order]
  if (!exerciseGroup) return workout
  if (exerciseGroup.length === 1) {
    newExerciseCollection.splice(exercise.ex_order, 1)
  } else {
    exerciseGroup.splice(exercise.sub_order, 1)
    newExerciseCollection[exercise.ex_order] = exerciseGroup
  }
  newExerciseCollection.forEach((group, groupIndex) => {
    group.forEach((item, itemIndex) => {
      item.ex_order = groupIndex
      item.sub_order = itemIndex
    })
  })
  return {
    ...workout,
    exerciseCollection: newExerciseCollection,
  }
}

function AddTemplateWorkoutMenu({
  disabled,
  onAdd,
  onImport,
}: {
  disabled?: boolean
  onAdd: () => void
  onImport: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          Add workout
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onAdd} disabled={disabled}>
          <Plus className="mr-2 h-4 w-4" />
          Workout
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onImport} disabled={disabled}>
          <Download className="mr-2 h-4 w-4" />
          From Library
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const Route = createFileRoute('/_auth/_dashboard/training/$templateId')({
  beforeLoad: () => {
    requirePermission('plans.write')
  },
  component: TrainingTemplateEditor,
  loader: ({ params }) =>
    trpcUtils.training.getPlan.ensureData({ planId: params.templateId }),
})

function TrainingTemplateEditor() {
  const { templateId } = Route.useParams()
  const { data: plan } = trpc.training.getPlan.useQuery({ planId: templateId })
  if (!plan) return <LoadingSpinner />
  return <TrainingTemplateEditorForm key={plan.id} plan={plan} />
}

function TrainingTemplateEditorForm({ plan }: { plan: TemplatePlanRow }) {
  const { setContent } = useNavbar()
  const navigate = useNavigate()
  const utils = trpc.useUtils()
  const savePlan = trpc.training.savePlan.useMutation()
  const deletePlan = trpc.training.deletePlan.useMutation()
  const { templateId } = Route.useParams()
  const linkedCount = plan.linkedCount
  const initialDoc = useMemo(() => toTemplateEditorDocument(plan), [plan.id])
  const history = useEditorHistory(initialDoc)
  const {
    planName,
    workouts,
    relativeScheduleItems,
    schedulingMode: planSchedulingMode,
    deletedWorkouts,
  } = history.present
  const hasChanges = history.hasChanges
  const [relativePageStartOffset, setRelativePageStartOffset] = useState(0)
  const [currentTab, setCurrentTab] = useState(
    () => initialDoc.workouts[0]?.id ?? '',
  )
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [importWorkoutDialogOpen, setImportWorkoutDialogOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useVersionHistoryOpen()
  const isPreviewing = useVersionHistoryPreview()
  const bottomRef = useRef<HTMLDivElement>(null)

  useDocumentTitle(planName || 'Training plan')

  useEffect(() => {
    if (workouts.some((workout) => workout.id === currentTab)) return
    setCurrentTab(workouts[0]?.id ?? '')
  }, [workouts, currentTab])

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/training">Training</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <Slash />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{planName || 'Template'}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    [planName],
  )

  useEffect(() => {
    setContent(breadcrumb)
    return () => setContent(null)
  }, [breadcrumb, setContent])

  const updateWorkout = useCallback(
    (nextWorkout: Workout, options?: EditorCommitOptions) => {
      history.commitDoc(
        (prev) => ({
          ...prev,
          workouts: prev.workouts.map((workout) =>
            workout.id === nextWorkout.id ? nextWorkout : workout,
          ),
        }),
        options,
      )
    },
    [history.commitDoc],
  )

  const handleUndo = useCallback(() => Boolean(history.undo()), [history.undo])
  const handleRedo = useCallback(() => Boolean(history.redo()), [history.redo])

  const addWorkout = () => {
    let workoutId = ''
    history.commitDoc((prev) => {
      const workout = emptyWorkout(prev.workouts.length)
      workoutId = workout.id
      return {
        ...prev,
        workouts: [...prev.workouts, workout],
      }
    })
    setCurrentTab(workoutId)
  }

  const handleImportWorkout = (workout: Workout) => {
    let workoutId = ''
    history.commitDoc((prev) => {
      const current = prev.workouts.find((item) => item.id === currentTab)
      const replaceEmpty =
        current != null && current.exerciseCollection.length === 0
      const cloned = cloneWorkoutAtDay(
        workout,
        replaceEmpty ? (current.day ?? prev.workouts.length) : prev.workouts.length,
      )
      workoutId = cloned.id
      if (!replaceEmpty || !current) {
        return {
          ...prev,
          workouts: [...prev.workouts, cloned],
        }
      }
      return {
        ...prev,
        workouts: prev.workouts.map((item) =>
          item.id === current.id ? cloned : item,
        ),
        deletedWorkouts:
          current.id.length > 2
            ? [...prev.deletedWorkouts, current.id]
            : prev.deletedWorkouts,
      }
    })
    setCurrentTab(workoutId)
    toast.success(`${workout.name} imported`)
  }

  const handleCreateWorkoutForDay = (dayOffset: number, name: string) => {
    let scheduleId = ''
    let workoutId = ''
    history.commitDoc((prev) => {
      const workout: Workout = {
        ...emptyWorkout(prev.workouts.length),
        name: name.trim() || 'New workout',
        description: '',
      }
      workoutId = workout.id
      const item = nextRelativeScheduleItem(
        prev.relativeScheduleItems,
        workout.id,
        dayOffset,
      )
      scheduleId = item.id ?? ''
      return {
        ...prev,
        workouts: [...prev.workouts, workout],
        relativeScheduleItems: [...prev.relativeScheduleItems, item],
      }
    })
    setCurrentTab(workoutId)
    return scheduleId
  }

  const handleImportWorkoutForDay = (dayOffset: number, workout: Workout) => {
    history.commitDoc((prev) => {
      const cloned = cloneWorkoutAtDay(workout, prev.workouts.length)
      const item = nextRelativeScheduleItem(
        prev.relativeScheduleItems,
        cloned.id,
        dayOffset,
      )
      return {
        ...prev,
        workouts: [...prev.workouts, cloned],
        relativeScheduleItems: [...prev.relativeScheduleItems, item],
      }
    })
  }

  const handleAddExistingWorkoutToDay = (
    dayOffset: number,
    workout: Workout,
  ) => {
    history.commitDoc((prev) => ({
      ...prev,
      relativeScheduleItems: [
        ...prev.relativeScheduleItems,
        nextRelativeScheduleItem(
          prev.relativeScheduleItems,
          workout.id,
          dayOffset,
        ),
      ],
    }))
  }

  const handleCopyWorkoutForDay = (dayOffset: number, workout: Workout) => {
    handleCopyWorkoutsForDays([{ dayOffset, workout }])
  }

  const handleCopyWorkoutsForDays = (
    entries: { dayOffset: number; workout: Workout }[],
  ) => {
    if (entries.length === 0) return
    history.commitDoc((prev) => {
      let nextWorkouts = prev.workouts
      let nextItems = prev.relativeScheduleItems
      entries.forEach((entry) => {
        const cloned = cloneWorkoutAtDay(entry.workout, nextWorkouts.length)
        const item = nextRelativeScheduleItem(
          nextItems,
          cloned.id,
          Math.max(0, entry.dayOffset),
        )
        nextWorkouts = [...nextWorkouts, cloned]
        nextItems = [...nextItems, item]
      })
      return {
        ...prev,
        workouts: nextWorkouts,
        relativeScheduleItems: nextItems,
      }
    })
  }

  const deleteWorkouts = (workoutIds: string[]) => {
    if (workoutIds.length === 0) return
    const idSet = new Set(workoutIds)
    let nextTab = currentTab
    history.commitDoc((prev) => {
      const remaining = prev.workouts.filter((workout) => !idSet.has(workout.id))
      const nextWorkouts =
        prev.schedulingMode !== 'dated' && remaining.length === 0
          ? [emptyWorkout(0)]
          : remaining
      nextTab = nextWorkouts.some((workout) => workout.id === currentTab)
        ? currentTab
        : (nextWorkouts[0]?.id ?? '')
      return {
        ...prev,
        workouts: nextWorkouts,
        deletedWorkouts: [...prev.deletedWorkouts, ...workoutIds],
        relativeScheduleItems: prev.relativeScheduleItems.filter(
          (item) => !idSet.has(item.workout_id),
        ),
      }
    })
    setCurrentTab(nextTab)
  }

  const removeExercise = (exercise: Exercise) => {
    const workout = workouts.find((item) => item.id === currentTab)
    if (!workout) return
    updateWorkout(removeExerciseFromWorkout(workout, exercise))
  }

  const handleExerciseSelectList = (exercise: Tables<'exercises'>) => {
    const workout = workouts.find((item) => item.id === currentTab)
    if (!workout) return
    const newExercise = createWorkoutExercise({
      exercise,
      exOrder: workout.exerciseCollection?.length ?? 0,
      subOrder: 0,
      sectionId: ensureWorkoutSections(workout).sections.at(-1)?.id,
    })
    updateWorkout({
      ...workout,
      exerciseCollection: [...workout.exerciseCollection, [newExercise]],
    })
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  const handleExerciseCreated = (exercise: Tables<'exercises'>) => {
    handleExerciseSelectList(exercise)
  }

  const deleteWorkout = (workoutId: string) => {
    let nextTab = currentTab
    history.commitDoc((prev) => {
      const remaining = prev.workouts.filter(
        (workout) => workout.id !== workoutId,
      )
      const nextWorkouts =
        prev.schedulingMode !== 'dated' && remaining.length === 0
          ? [emptyWorkout(0)]
          : remaining
      nextTab = nextWorkouts.some((workout) => workout.id === currentTab)
        ? currentTab
        : (nextWorkouts[0]?.id ?? '')
      return {
        ...prev,
        workouts: nextWorkouts,
        deletedWorkouts: [...prev.deletedWorkouts, workoutId],
        relativeScheduleItems: prev.relativeScheduleItems.filter(
          (item) => item.workout_id !== workoutId,
        ),
      }
    })
    setCurrentTab(nextTab)
  }

  const saveTemplate = async () => {
    const orgId = store.getState().session.org?.id
    if (!orgId) return
    if (!planName.trim()) {
      toast.error('Please enter a template name.')
      return
    }

    const ordered = [...workouts].sort((a, b) => (a.day ?? 0) - (b.day ?? 0))
    const scheduledWorkoutIds = new Set(
      relativeScheduleItems.map((entry) => entry.workout_id),
    )
    const workoutsForSave =
      planSchedulingMode === 'dated'
        ? ordered.filter((workout) => scheduledWorkoutIds.has(workout.id))
        : ordered
    const autoDeletedUnscheduleWorkoutIds =
      planSchedulingMode === 'dated'
        ? ordered
            .filter((workout) => !scheduledWorkoutIds.has(workout.id))
            .map((workout) => workout.id)
        : []

    const workoutPayload = workoutsForSave.map((workout) => ({
      ...editorWorkoutToSavePayload(workout).workout,
      day: workout.day ?? 0,
    }))

    const rpcParams = {
      org_id: orgId,
      workouts: workoutPayload,
      deleted_workouts: Array.from(
        new Set([...deletedWorkouts, ...autoDeletedUnscheduleWorkoutIds]),
      ),
      planname: planName,
      p_client_id: null,
      planid: plan?.id ?? templateId,
      is_primary: false,
      is_visible: true,
      p_is_template: true,
      p_scheduling_mode: planSchedulingMode,
      p_schedule_entries:
        planSchedulingMode === 'dated'
          ? relativeScheduleItems.map((entry) => ({
              workout_id: entry.workout_id,
              schedule_basis: 'relative',
              day_offset: entry.day_offset,
              order_index: entry.order_index ?? 0,
            }))
          : [],
      p_fixed_schedule_entries: [],
    } as Record<string, unknown>

    try {
      await savePlan.mutateAsync(toTrainingSavePlanInput(rpcParams))
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return
    }

    const savedPlanId = plan?.id ?? templateId
    patchListCache(utils.training.listTemplates, (old) =>
      old.map((row) =>
        row.id === savedPlanId
          ? {
              ...row,
              name: planName,
              workoutCount: workouts.length,
            }
          : row,
      ),
    )

    toast.success('Template saved')
    history.rebaseline()
  }

  const handleDeleteTemplate = async () => {
    const planId = plan?.id ?? templateId
    try {
      await deletePlan.mutateAsync({ planId })
    } catch (error) {
      console.error(error)
      errorToast(error instanceof Error ? error : null)
      return
    }
    patchListCache(utils.training.listTemplates, (old) =>
      removeFromList(old, planId),
    )
    toast.success('Template deleted')
    history.rebaseline()
    navigate({ to: '/training' })
  }

  const orderedWorkouts = useMemo(
    () => [...workouts].sort((a, b) => (a.day ?? 0) - (b.day ?? 0)),
    [workouts],
  )

  return (
    <VersionHistorySheet
      kind="training"
      planId={plan?.id}
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      hasChanges={hasChanges}
      liveSnapshot={liveTrainingSnapshotFromEditor({
        planId: plan?.id,
        name: planName,
        schedulingMode: planSchedulingMode,
        workouts,
        relativeSchedule: relativeScheduleItems,
      })}
      onApplyRevision={(snapshot: TrainingRevisionSnapshot, options) => {
        const nextWorkouts = workoutsFromTrainingSnapshot(snapshot)
        const schedule = scheduleFromTrainingSnapshot(snapshot)
        history.reset(
          toTrainingTemplateEditorDocument({
            planName: snapshot.plan.name,
            workouts: nextWorkouts,
            relativeScheduleItems: schedule.relativeSchedule,
            schedulingMode:
              schedule.schedulingMode === 'dated' ? 'dated' : 'fixed',
            deletedWorkouts: extraStoredIds(
              workouts,
              nextWorkouts,
              deletedWorkouts,
            ),
          }),
          { markDirty: options.markDirty },
        )
        setCurrentTab(nextWorkouts[0]?.id ?? '')
      }}
    >
      <AlertBlocker shouldBlock={hasChanges} />
      <div className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div
          className={cn(
            editorPageRailClassName,
            'flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between',
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <InputFieldEditButton
              value={planName}
              setValue={(value) => {
                history.commitDoc(
                  (prev) => ({ ...prev, planName: value }),
                  { coalesceKey: 'plan-name' },
                )
              }}
              disabled={isPreviewing}
              placeholder="Template name"
              className="min-w-0 flex-1 text-2xl font-bold"
            />
            <PlanVersionTag />
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">
            <EditorUndoControls
              canUndo={history.canUndo}
              canRedo={history.canRedo}
              disabled={isPreviewing}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
            <PlanHistoryButton
              disabled={!plan?.id}
              onClick={() => setHistoryOpen(true)}
            />
            <ButtonGroup className="shrink-0 flex-nowrap whitespace-nowrap">
              <Button
                onClick={saveTemplate}
                disabled={!hasChanges || isPreviewing}
                variant="outline"
              >
                Save
              </Button>
              <ButtonGroupSeparator />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setDeleteOpen(true)}
                    variant="destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Template
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          </div>
        </div>
      </div>

      <div className={cn(editorPageRailClassName, 'mt-4 pb-8')}>
      {planSchedulingMode === 'fixed' ? (
        orderedWorkouts.length === 0 ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyTitle>No workouts yet</EmptyTitle>
              <EmptyDescription>
                Add one to start building the plan.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <AddTemplateWorkoutMenu
                disabled={isPreviewing}
                onAdd={addWorkout}
                onImport={() => setImportWorkoutDialogOpen(true)}
              />
            </EmptyContent>
          </Empty>
        ) : (
        <>
          <div className="mb-6 flex items-center justify-between gap-3">
            <Tabs
              value={currentTab}
              onValueChange={setCurrentTab}
              className="flex-1"
            >
              <ScrollArea>
                <TabsList className="w-max">
                  {orderedWorkouts.map((workout) => (
                    <TabsTrigger key={workout.id} value={workout.id}>
                      {workout.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </Tabs>
            <AddTemplateWorkoutMenu
              disabled={isPreviewing}
              onAdd={addWorkout}
              onImport={() => setImportWorkoutDialogOpen(true)}
            />
          </div>

          {orderedWorkouts
            .filter((workout) => workout.id === currentTab)
            .map((workout) => (
              <WorkoutEditorWorkspace
                key={workout.id}
                workout={workout}
                updateWorkout={updateWorkout}
                setHasChanges={() => {}}
                className={editorPageWorkspaceClassName}
              >
                <div className="min-w-0 flex-1">
                  <EditWorkoutView
                    workout={workout}
                    updateWorkout={updateWorkout}
                    name={{
                      value: workout.name ?? '',
                      valueChange: (value) => {
                        updateWorkout(
                          { ...workout, name: value },
                          { coalesceKey: `workout:${workout.id}:name` },
                        )
                      },
                    }}
                    description={{
                      value: workout.description ?? '',
                      valueChange: (value) => {
                        updateWorkout(
                          { ...workout, description: value },
                          { coalesceKey: `workout:${workout.id}:description` },
                        )
                      },
                    }}
                    hasChanges={hasChanges}
                    setHasChanges={() => {}}
                    removeExercise={removeExercise}
                    deleteWorkout={() => deleteWorkout(workout.id)}
                  />
                  <div ref={bottomRef} />
                  {isPreviewing ? null : (
                    <AiWorkoutBar
                      exerciseCollection={workout.exerciseCollection}
                      commit={(producer) => {
                        history.commitDoc((prev) => ({
                          ...prev,
                          workouts: prev.workouts.map((item) =>
                            item.id === workout.id ? producer(item) : item,
                          ),
                        }))
                      }}
                      onEdited={() => {
                        setTimeout(() => {
                          bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
                        }, 200)
                      }}
                    />
                  )}
                </div>
                <ExerciseSelectList
                  exerciseSelected={handleExerciseSelectList}
                  enableDragToWorkout
                  onExerciseCreated={handleExerciseCreated}
                  className={`hidden xl:flex client-chat-hide-exercise-select ${WORKOUT_SIDEBAR_STICKY_CLASS}`}
                />
              </WorkoutEditorWorkspace>
            ))}
        </>
        )
      ) : (
        <div className="w-full">
          <ClientTrainingCalendarView
            planSchedulingMode="dated"
            scheduleContext="template-relative"
            workouts={orderedWorkouts}
            scheduleItems={[]}
            fixedScheduleItems={[]}
            relativeScheduleItems={relativeScheduleItems as RelativeScheduleItem[]}
            fixedScheduleViewWeeks={1}
            relativePageSize={28}
            relativePageStartOffset={relativePageStartOffset}
            onRelativePageStartOffsetChange={setRelativePageStartOffset}
            hasChanges={hasChanges}
            setHasChanges={() => {}}
            onScheduleItemsChange={() => {}}
            onFixedScheduleItemsChange={() => {}}
            onRelativeScheduleItemsChange={(items) => {
              history.commitDoc(
                (prev) => ({ ...prev, relativeScheduleItems: items }),
                { coalesceKey: 'relative-schedule' },
              )
            }}
            onCreateWorkoutForDate={() => undefined}
            onImportWorkoutForDate={() => {}}
            onCopyWorkoutForDate={() => {}}
            onCreateWorkoutForDay={handleCreateWorkoutForDay}
            onImportWorkoutForDay={handleImportWorkoutForDay}
            onCopyWorkoutForDay={handleCopyWorkoutForDay}
            onCopyWorkoutsForDay={(entries) =>
              handleCopyWorkoutsForDays(
                entries.map((entry) => ({
                  dayOffset: entry.dayIndex,
                  workout: entry.workout,
                })),
              )
            }
            onAddExistingWorkoutToDay={handleAddExistingWorkoutToDay}
            onFixedScheduleViewWeeksChange={() => {}}
            onSave={saveTemplate}
            saveDisabled={!hasChanges || isPreviewing}
            onWorkoutUpdated={(workout) => {
              updateWorkout(workout)
            }}
            commitWorkout={(workoutId, producer) => {
              history.commitDoc((prev) => ({
                ...prev,
                workouts: prev.workouts.map((item) =>
                  item.id === workoutId ? producer(item) : item,
                ),
              }))
            }}
            onDeleteWorkout={(workout) => deleteWorkout(workout.id)}
            onDeleteWorkouts={(workouts) =>
              deleteWorkouts(workouts.map((workout) => workout.id))
            }
            onRemoveExerciseFromWorkout={(workoutId, exercise) => {
              const targetWorkout = workouts.find((item) => item.id === workoutId)
              if (!targetWorkout) return
              updateWorkout(removeExerciseFromWorkout(targetWorkout, exercise))
            }}
          />
        </div>
      )}
      </div>
      <ImportWorkoutDialog
        open={importWorkoutDialogOpen}
        onOpenChange={setImportWorkoutDialogOpen}
        onWorkoutSelected={handleImportWorkout}
      />
      <DeleteAlert
        title={`Delete ${planName || 'template'}?`}
        description={`This will remove the plan from ${linkedCount} ${
          linkedCount === 1 ? 'client' : 'clients'
        } that are linked. Are you sure you want to delete this training template?`}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onDelete={handleDeleteTemplate}
      />
    </VersionHistorySheet>
  )
}
